import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const myths = [
  {
    icon: 'ri-ruler-line',
    myth: '"Si levanta pesas se va a quedar bajito porque se le cierran las placas de crecimiento"',
    truth: 'Porque el entrenamiento de fuerza no es solo "levantar pesas", no es solo "mover grandes pesos", es proporcionar el estímulo mecánico controlado que el hueso joven necesita para fortalecerse.',
    color: 'from-[#185a7d] to-[#1a6a92]',
    accent: '#d1b168',
    label: 'Mito #1',
  },
  {
    icon: 'ri-run-line',
    myth: '"Este niño es muy patoso y no tiene coordinación, mejor que no coja peso..."',
    truth: 'Entender que la fuerza es la llave que desbloquea la coordinación: sin una base sólida de fuerza, el sistema nervioso no tiene la estabilidad necesaria para ejecutar movimientos complejos…',
    color: 'from-[#1a6a92] to-[#185a7d]',
    accent: '#d1b168',
    label: 'Mito #2',
  },
  {
    icon: 'ri-trophy-line',
    myth: '"Como solo juega al tenis, que solo haga ejercicios de tenis; no quiero que gane músculo innecesario..."',
    truth: 'Al contrario: la especialización temprana sin una base de fuerza general es la vía rápida hacia las lesiones por sobreuso, aburrimiento, y reducción de rendimiento.',
    color: 'from-[#185a7d] to-[#1a6a92]',
    accent: '#d1b168',
    label: 'Mito #3',
  },
  {
    icon: 'ri-search-eye-line',
    myth: '"Total, son niños… tampoco hace falta evaluar mucho."',
    truth: 'Porque sin valorar su nivel de capacidad motriz o sus capacidades físicas, estás entrenando a ciegas.',
    color: 'from-[#1a6a92] to-[#185a7d]',
    accent: '#d1b168',
    label: 'Mito #4',
  },
  {
    icon: 'ri-lightbulb-line',
    myth: '"Primero que aprendan la técnica, ya meteremos el trabajo físico más adelante."',
    truth: 'El desarrollo de la técnica tiene también requerimientos de fuerza. Separarlos no tiene sentido si quieres progresar y no lesionarte.',
    color: 'from-[#185a7d] to-[#1a6a92]',
    accent: '#d1b168',
    label: 'Mito #5',
  },
  {
    icon: 'ri-heart-pulse-line',
    myth: '"Si no compite a alto nivel, no necesita trabajo de fuerza."',
    truth: 'Porque la fuerza no es solo un elemento que mejora el rendimiento: es prevención y es salud a largo plazo.',
    color: 'from-[#1a6a92] to-[#185a7d]',
    accent: '#d1b168',
    label: 'Mito #6',
  },
  {
    icon: 'ri-shield-cross-line',
    myth: '"Mejor que no haga fuerza, que todavía está creciendo y le puedo hacer daño..."',
    truth: 'Cuando en realidad, lo que estás haciendo es privarle de desarrollar una base física segura y necesaria para su deporte y su salud.',
    color: 'from-[#185a7d] to-[#1a6a92]',
    accent: '#d1b168',
    label: 'Mito #7',
  },
  {
    icon: 'ri-question-line',
    myth: '"Que haga unas sentadillas y unas flexiones al final del entreno, sin complicarnos mucho..."',
    truth: 'Aquí no estás entrenando fuerza… estás improvisando sin saber realmente qué necesita ese niño o niña.',
    color: 'from-[#1a6a92] to-[#185a7d]',
    accent: '#d1b168',
    label: 'Mito #8',
  },
  {
    icon: 'ri-time-line',
    myth: '"Ya tendrá tiempo de entrenar fuerza cuando sea mayor, ahora que juegue..."',
    truth: 'Porque jugar también es correr, saltar, frenar… y todo eso es fuerza, aunque no la estés entrenando bien.',
    color: 'from-[#185a7d] to-[#1a6a92]',
    accent: '#d1b168',
    label: 'Mito #9',
  },
];

function StackedCards() {
  const [current, setCurrent] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontal = useRef<boolean | null>(null);

  const total = myths.length;
  const SWIPE_THRESHOLD = 60;

  const goTo = useCallback((idx: number, dir: 'left' | 'right') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setTimeout(() => {
      setCurrent(idx);
      setDragOffset(0);
      setDirection(null);
      setIsAnimating(false);
    }, 320);
  }, [isAnimating]);

  const handleNext = useCallback(() => {
    if (current < total - 1) goTo(current + 1, 'left');
  }, [current, total, goTo]);

  const handlePrev = useCallback(() => {
    if (current > 0) goTo(current - 1, 'right');
  }, [current, goTo]);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isHorizontal.current = null;
    setIsDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (isHorizontal.current === null) {
      isHorizontal.current = Math.abs(dx) > Math.abs(dy);
    }
    if (isHorizontal.current) {
      e.preventDefault();
      setDragOffset(dx);
    }
  };

  const onTouchEnd = () => {
    setIsDragging(false);
    if (isHorizontal.current) {
      if (dragOffset < -SWIPE_THRESHOLD && current < total - 1) {
        handleNext();
      } else if (dragOffset > SWIPE_THRESHOLD && current > 0) {
        handlePrev();
      } else {
        setDragOffset(0);
      }
    } else {
      setDragOffset(0);
    }
    isHorizontal.current = null;
  };

  const onMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    setIsDragging(true);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - startX.current);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    if (dragOffset < -SWIPE_THRESHOLD && current < total - 1) {
      handleNext();
    } else if (dragOffset > SWIPE_THRESHOLD && current > 0) {
      handlePrev();
    } else {
      setDragOffset(0);
    }
  };

  const getCardStyle = (offset: number): React.CSSProperties => {
    const scale = 1 - offset * 0.05;
    const translateY = offset * 10;
    const opacity = 1 - offset * 0.15;
    return {
      transform: `scale(${scale}) translateY(${translateY}px)`,
      opacity,
      zIndex: 10 - offset,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    };
  };

  const visibleBehind = Math.min(3, total - current - 1);

  return (
    <div className="px-6 pb-8">
      <style>{`
        .myth-card-front {
          transition: transform 0.05s linear;
          cursor: grab;
          user-select: none;
        }
        .myth-card-front:active {
          cursor: grabbing;
        }
        .myth-card-exit-left {
          animation: exitLeft 0.32s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        .myth-card-exit-right {
          animation: exitRight 0.32s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        @keyframes exitLeft {
          to { transform: translateX(-110%) rotate(-8deg); opacity: 0; }
        }
        @keyframes exitRight {
          to { transform: translateX(110%) rotate(8deg); opacity: 0; }
        }
        .card-chrome-stack {
          background: white;
          border: 2px solid transparent;
          background-clip: padding-box;
          position: relative;
        }
        .card-chrome-stack::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 1rem;
          padding: 2px;
          background: linear-gradient(135deg, #185a7d, #d1b168, #185a7d);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0.4;
        }
      `}</style>

      {/* Stack container — altura dinámica según contenido */}
      <div
        className="relative mx-auto"
        style={{ maxWidth: '420px' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Tarjetas de fondo (efecto pila) — posicionadas absolutas detrás */}
        <div className="relative" style={{ paddingBottom: `${visibleBehind * 10}px` }}>
          {[...Array(visibleBehind)].map((_, i) => {
            const offset = visibleBehind - i;
            return (
              <div
                key={`behind-${current + offset}`}
                className="card-chrome-stack rounded-xl shadow-md w-full absolute inset-0"
                style={{
                  transform: `scale(${1 - offset * 0.04}) translateY(${offset * 10}px)`,
                  opacity: 1 - offset * 0.15,
                  zIndex: 10 - offset,
                  background: 'white',
                }}
              />
            );
          })}

          {/* Tarjeta principal */}
          <div
            className={`card-chrome-stack myth-card-front rounded-xl shadow-2xl w-full relative ${
              isAnimating && direction === 'left' ? 'myth-card-exit-left' :
              isAnimating && direction === 'right' ? 'myth-card-exit-right' : ''
            }`}
            style={{
              zIndex: 20,
              transform: !isAnimating ? `translateX(${dragOffset}px) rotate(${dragOffset * 0.03}deg)` : undefined,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
            }}
          >
            <div className="flex flex-col p-5 rounded-xl">
              {/* Cabecera */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black uppercase tracking-widest text-[#185a7d] bg-[#185a7d]/10 px-3 py-1 rounded-full">
                  {myths[current].label}
                </span>
                <span className="text-xs text-gray-400 font-medium">{current + 1} / {total}</span>
              </div>

              {/* Icono + Mito */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${myths[current].color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <i className={`${myths[current].icon} text-2xl text-white`}></i>
                </div>
                <p className="text-base font-black text-gray-900 leading-snug italic">
                  {myths[current].myth}
                </p>
              </div>

              {/* Separador */}
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-[#185a7d]/20 to-transparent"></div>
                <div className="w-6 h-6 bg-gradient-to-br from-[#d1b168] to-[#c4a158] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-down-line text-white text-sm"></i>
                </div>
                <div className="h-px flex-1 bg-gradient-to-l from-[#185a7d]/20 to-transparent"></div>
              </div>

              {/* Verdad científica */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {myths[current].truth}
              </p>

              {/* Indicador de deslizamiento */}
              <div className="flex items-center justify-center gap-1 opacity-40 pt-1">
                <i className="ri-arrow-left-s-line text-gray-400 text-sm"></i>
                <span className="text-xs text-gray-400">desliza</span>
                <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Puntos indicadores */}
      <div className="flex items-center justify-center gap-1.5 mt-6">
        {myths.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (i > current) goTo(i, 'left');
              else if (i < current) goTo(i, 'right');
            }}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              i === current
                ? 'w-6 h-2 bg-[#185a7d]'
                : i < current
                ? 'w-2 h-2 bg-[#d1b168]'
                : 'w-2 h-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Botones de navegación */}
      <div className="flex items-center justify-center gap-4 mt-4">
        <button
          onClick={handlePrev}
          disabled={current === 0}
          className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#185a7d]/30 text-[#185a7d] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all hover:bg-[#185a7d]/10"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <span className="text-sm font-semibold text-gray-500">{current + 1} de {total}</span>
        <button
          onClick={handleNext}
          disabled={current === total - 1}
          className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#185a7d]/30 text-[#185a7d] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all hover:bg-[#185a7d]/10"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}

export default function Registro() {
  const navigate = useNavigate();
  const [formData1, setFormData1] = useState({ nombre: '', email: '', check: false });
  const [formData2, setFormData2] = useState({ nombre: '', email: '', check: false });
  const [isSubmitting1, setIsSubmitting1] = useState(false);
  const [isSubmitting2, setIsSubmitting2] = useState(false);
  const [submitStatus1, setSubmitStatus1] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitStatus2, setSubmitStatus2] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-registro';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Certificado de Fuerza Pediátrica NSCA Spain",
      "description": "Único certificado oficial en español avalado por NSCA Spain sobre entrenamiento de fuerza en niños y jóvenes. Basado en evidencia científica.",
      "provider": {
        "@type": "Organization",
        "name": "NSCA Spain",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com"
      },
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "inLanguage": "es",
      "educationalLevel": "Professional",
      "teaches": "Entrenamiento de fuerza pediátrica, fuerza en niños y jóvenes, certificación oficial NSCA Spain",
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "online",
        "name": "2ª Edición — Certificado Fuerza Pediátrica"
      },
      "image": "https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d6831eafe8086c2846b34ced53874655.png",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/LimitedAvailability",
        "priceCurrency": "EUR",
        "validThrough": "2025-03-05"
      }
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('schema-registro');
      if (existing) existing.remove();
    };
  }, []);

  const handleSubmit1 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData1.nombre || !formData1.email) return;

    setIsSubmitting1(true);
    setSubmitStatus1('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formData1.nombre,
          email: formData1.email,
          quiero_informacion: formData1.check,
          formulario: 'hero',
        }),
      });

      if (response.ok) {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead', {
            content_name: 'Certificado Fuerza Pediátrica NSCA Spain',
            content_category: 'Formulario Hero',
          });
        }
        setSubmitStatus1('success');
        setTimeout(() => { navigate('/gracias'); }, 1500);
      } else {
        setSubmitStatus1('error');
      }
    } catch (error) {
      setSubmitStatus1('error');
    } finally {
      setIsSubmitting1(false);
    }
  };

  const handleSubmit2 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData2.nombre || !formData2.email) return;

    setIsSubmitting2(true);
    setSubmitStatus2('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formData2.nombre,
          email: formData2.email,
          quiero_informacion: formData2.check,
          formulario: 'cta-final',
        }),
      });

      if (response.ok) {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead', {
            content_name: 'Certificado Fuerza Pediátrica NSCA Spain',
            content_category: 'Formulario CTA Final',
          });
        }
        setSubmitStatus2('success');
        setTimeout(() => { navigate('/gracias'); }, 1500);
      } else {
        setSubmitStatus2('error');
      }
    } catch (error) {
      setSubmitStatus2('error');
    } finally {
      setIsSubmitting2(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section con Formulario */}
      <section className="relative bg-gradient-to-br from-[#185a7d] via-[#1a6a92] to-[#185a7d] py-10 px-4 lg:py-16 lg:px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d1b168] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Título arriba */}
          <div className="text-center mb-8">
            <h1 className="text-3xl lg:text-6xl font-black text-white leading-tight max-w-4xl mx-auto mb-4">
              Entrena fuerza en niños y jóvenes
            </h1>
            <h2 className="text-2xl lg:text-4xl font-bold text-[#d1b168] mb-6 drop-shadow-lg">
              El Certificado que Transforma tu Carrera desde la evidencia científica
            </h2>
            <div className="inline-flex items-center gap-2 bg-[#d1b168]/20 border border-[#d1b168]/40 rounded-full px-4 py-2">
              <i className="ri-award-line text-[#d1b168]"></i>
              <span className="text-sm font-bold text-[#d1b168]">Certificado Oficial NSCA Spain</span>
            </div>
          </div>

          {/* Imagen primero en móvil, formulario segundo */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">

            {/* Formulario — segundo en móvil, segundo en desktop */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8 flex flex-col justify-center order-2 lg:order-2">
              <div className="text-center mb-5">
                <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1.5 mb-3">
                  <i className="ri-fire-line text-red-500 text-sm"></i>
                  <span className="text-xs font-bold text-red-600">1ª Edición agotada en 10 días</span>
                </div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">
                  Quiero Información de la 2ª Edición
                </h2>
                <p className="text-gray-600 text-sm">
                  Plazas muy limitadas — Sé el primero en enterarte
                </p>
              </div>

              <form id="form-registro-1" data-readdy-form onSubmit={handleSubmit1} className="space-y-4">
                <div>
                  <label htmlFor="nombre1" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    id="nombre1"
                    name="nombre"
                    value={formData1.nombre}
                    onChange={(e) => setFormData1({ ...formData1, nombre: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#185a7d] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="email1" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email1"
                    name="email"
                    value={formData1.email}
                    onChange={(e) => setFormData1({ ...formData1, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#185a7d] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Tu mejor email :)"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="check1"
                    name="quiero_informacion"
                    checked={formData1.check}
                    onChange={(e) => setFormData1({ ...formData1, check: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#185a7d] cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="check1" className="text-sm text-gray-700 cursor-pointer leading-snug">
                    Quiero recibir información del <strong>Certificado de fuerza en niños y jóvenes</strong>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting1}
                  className="w-full bg-gradient-to-r from-[#d1b168] to-[#c4a158] text-white font-bold py-4 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm lg:text-base"
                >
                  {isSubmitting1 ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 flex-wrap">
                      Quiero Información de la 2ª Edición
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                </button>

                {submitStatus1 === 'success' && (
                  <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2">
                    <i className="ri-time-line text-amber-500 text-base flex-shrink-0"></i>
                    Espera a finalizar tu registro...
                  </div>
                )}
                {submitStatus1 === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    Hubo un error. Por favor, intenta de nuevo.
                  </div>
                )}
              </form>

              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <i className="ri-lock-line"></i>
                  <span>100% Seguro</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-shield-check-line"></i>
                  <span>Sin Spam</span>
                </div>
              </div>
            </div>

            {/* Imagen — primera en móvil, primera en desktop */}
            <div className="flex flex-col gap-4 order-1 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: '320px' }}>
                <div className="absolute -inset-4 bg-[#d1b168]/20 rounded-3xl blur-2xl z-0"></div>
                <img
                  src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d6831eafe8086c2846b34ced53874655.png"
                  alt="Jóvenes deportistas NSCA Spain YSCC"
                  className="relative z-10 w-full h-full object-cover object-top drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
                  style={{ minHeight: '320px' }}
                />
              </div>

              {/* Info debajo de la imagen */}
              <div className="flex flex-col gap-3">
                <div className="inline-flex items-center gap-2 bg-[#d1b168] rounded-full px-4 py-2 self-start">
                  <i className="ri-award-fill text-white text-sm"></i>
                  <span className="text-xs font-black text-white uppercase tracking-wide">Formación Única en Español</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sección de Necesitas el Certificado Si... */}
      <section className="py-20 px-0 lg:px-6 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 px-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Necesitas el Certificado de Fuerza Pediátrica si alguna vez has pensado...
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Desmonta mitos y aprende la verdad científica sobre el entrenamiento de fuerza en niños y jóvenes
            </p>
          </div>

          {/* Cromos apilados en móvil */}
          <div className="lg:hidden">
            <StackedCards />
          </div>

          {/* Grid en desktop */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-6 px-6">
            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-ruler-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Si levanta pesas se va a quedar bajito porque se le cierran las placas de crecimiento"
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Porque el entrenamiento de fuerza no es solo "levantar pesas", no es solo "mover grandes pesos", es proporcionar el estímulo mecánico controlado que el hueso joven necesita para fortalecerse.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-run-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Este niño es muy patoso y no tiene coordinación, mejor que no coja peso..."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Entender que la fuerza es la llave que desbloquea la coordinación: sin una base sólida de fuerza, el sistema nervioso no tiene la estabilidad necesaria para ejecutar movimientos complejos…
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Como solo juega al tenis, que solo haga ejercicios de tenis; no quiero que gane músculo innecesario..."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Al contrario: la especialización temprana sin una base de fuerza general es la vía rápida hacia las lesiones por sobreuso, aburrimiento, y reducción de rendimiento. Un desarrollo atlético debe plantearse como integral y largo plazo donde la fuerza es el pegamento que une la técnica con la prevención de lesiones y el rendimiento.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-eye-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Total, son niños… tampoco hace falta evaluar mucho."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Porque sin valorar su nivel de capacidad motriz o sus capacidades físicas, estás entrenando a ciegas.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Primero que aprendan la técnica, ya meteremos el trabajo físico más adelante."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                El desarrollo de la técnica tiene también requerimientos de fuerza. Separarlos no tiene sentido si quieres progresar y no lesionarte.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Si no compite a alto nivel, no necesita trabajo de fuerza."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Porque la fuerza no es solo un elemento que mejora el rendimiento: es prevención y es salud a largo plazo.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-cross-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Mejor que no haga fuerza, que todavía está creciendo y le puedo hacer daño..."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Cuando en realidad, lo que estás haciendo es privarle de desarrollar una base física segura y necesaria para su deporte y su salud.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Que haga unas sentadillas y unas flexiones al final del entreno, sin complicarnos mucho..."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Aquí no estás entrenando fuerza… estás improvisando sin saber realmente qué necesita ese niño o niña.
              </p>
            </div>

            <div className="card-chrome rounded-xl p-6 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#185a7d] to-[#1a6a92] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">
                  "Ya tendrá tiempo de entrenar fuerza cuando sea mayor, ahora que juegue..."
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Porque jugar también es correr, saltar, frenar… y todo eso es fuerza, aunque no la estés entrenando bien.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de Solución */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              NSCA Spain y su Certificado Pueden Resolver Esto
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              No es solo otro curso online. Es la única certificación oficial en español avalada por NSCA Spain
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/ebe8c80d051445d263d01d90a6ad6fa4.png"
                alt="Entrenamiento de fuerza pediátrico"
                className="w-full h-[400px] object-cover object-top rounded-2xl shadow-xl"
              />
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#185a7d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-star-line text-2xl text-[#185a7d]"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Único Certificado Oficial en Español
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Somos el único programa certificado por NSCA Spain especializado en entrenamiento de fuerza pediátrica. No encontrarás nada igual en el mercado hispanohablante.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#185a7d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-2xl text-[#185a7d]"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Expertos de Talla Mundial
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Aprende directamente del PhD Avery D. Faigenbaum, LA referencia mundial en fuerza pediátrica, y un equipo de doctores con décadas de experiencia.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#185a7d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-microscope-line text-2xl text-[#185a7d]"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Metodología Basada en Evidencia
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Todo el contenido está respaldado por la investigación científica más reciente. Aprenderás protocolos validados, no opiniones.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#185a7d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-hand-heart-line text-2xl text-[#185a7d]"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Aplicación Práctica Inmediata
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Desde el primer módulo podrás aplicar lo aprendido con tus deportistas. Casos prácticos reales y herramientas listas para usar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de Autoridad NSCA */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#185a7d] to-[#1a6a92]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <img 
              src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/e1e681509551cb55badc2894ba94db57.png"
              alt="NSCA Spain"
              className="h-24 w-auto mx-auto mb-6"
            />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              ¿Quién es NSCA Spain?
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              La organización líder mundial en fuerza y acondicionamiento físico
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <div className="w-14 h-14 bg-[#d1b168] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-global-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Reconocimiento Internacional
              </h3>
              <p className="text-white/80 leading-relaxed">
                NSCA (National Strength and Conditioning Association) es la organización más prestigiosa del mundo en entrenamiento de fuerza y acondicionamiento físico, con presencia en más de 70 países.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <div className="w-14 h-14 bg-[#d1b168] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Más de 35.000 Alumnos en España
              </h3>
              <p className="text-white/80 leading-relaxed">
                NSCA Spain lleva más de una década formando a los mejores profesionales del sector en España, con más de 35.000 alumnos certificados que trabajan en clubes de élite, centros deportivos y equipos profesionales.
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-black text-[#d1b168] mb-2">+40 años</div>
                <div className="text-white/80 text-sm">80 Países</div>
              </div>
              <div>
                <div className="text-4xl font-black text-[#d1b168] mb-2">35.000+</div>
                <div className="text-white/80 text-sm">Alumnos en España</div>
              </div>
              <div>
                <div className="text-4xl font-black text-[#d1b168] mb-2">#1</div>
                <div className="text-white/80 text-sm">Certificando a la élite</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Segundo Formulario */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-6">
                <i className="ri-fire-fill text-red-500"></i>
                <span className="text-sm font-bold text-red-600">1ª Edición agotada en 10 días</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4 leading-tight">
                El momento de actuar<br /><span className="text-[#185a7d]">es ahora.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Los profesionales que marcan la diferencia no esperan. Asegura tu plaza en la 2ª edición antes de que se agote.
              </p>

              <div className="mt-6 grid grid-cols-2 gap-4 text-center">
                <div className="bg-[#185a7d]/5 rounded-xl p-3">
                  <div className="text-2xl font-black text-[#185a7d]">10</div>
                  <div className="text-xs text-gray-500 font-medium">días tardó en agotarse</div>
                </div>
                <div className="bg-[#d1b168]/10 rounded-xl p-3">
                  <div className="text-2xl font-black text-[#d1b168]">100%</div>
                  <div className="text-xs text-gray-500 font-medium">basado en evidencia</div>
                </div>
              </div>
            </div>

            <form id="form-registro-2" data-readdy-form onSubmit={handleSubmit2} className="space-y-5">
              <div>
                <label htmlFor="nombre2" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  id="nombre2"
                  name="nombre"
                  value={formData2.nombre}
                  onChange={(e) => setFormData2({ ...formData2, nombre: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#185a7d] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div>
                <label htmlFor="email2" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email2"
                  name="email"
                  value={formData2.email}
                  onChange={(e) => setFormData2({ ...formData2, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#185a7d] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Tu mejor email :)"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="check2"
                  name="quiero_informacion"
                  checked={formData2.check}
                  onChange={(e) => setFormData2({ ...formData2, check: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#185a7d] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="check2" className="text-sm text-gray-700 cursor-pointer leading-snug">
                  Quiero recibir información del <strong>Certificado de fuerza en niños y jóvenes</strong>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting2}
                className="w-full bg-gradient-to-r from-[#d1b168] to-[#c4a158] text-white font-bold py-4 rounded-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm lg:text-base"
              >
                {isSubmitting2 ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 flex-wrap">
                    Quiero Información de la 2ª Edición
                    <i className="ri-arrow-right-line"></i>
                  </span>
                )}
              </button>

              {submitStatus2 === 'success' && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-base flex-shrink-0"></i>
                  Espera a finalizar tu registro...
                </div>
              )}
              {submitStatus2 === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  Hubo un error. Por favor, intenta de nuevo.
                </div>
              )}
            </form>

            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="bg-gradient-to-br from-[#185a7d]/5 to-[#d1b168]/10 rounded-2xl p-6 text-center relative overflow-hidden">
                <div className="absolute top-3 left-4 text-[#d1b168]/30 text-6xl font-serif leading-none select-none">&ldquo;</div>
                <div className="absolute bottom-3 right-4 text-[#d1b168]/30 text-6xl font-serif leading-none select-none">&rdquo;</div>
                <p className="text-lg lg:text-xl font-bold text-[#185a7d] italic leading-relaxed relative z-10 px-4">
                  La ventana de oportunidad para desarrollar la fuerza en la infancia no se repite. No la desaproveches
                </p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <div className="h-px w-8 bg-[#d1b168]"></div>
                  <span className="text-xs font-semibold text-[#d1b168] uppercase tracking-widest">NSCA Spain</span>
                  <div className="h-px w-8 bg-[#d1b168]"></div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#185a7d]"></i>
                  <span>Certificado Oficial</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-lock-line text-[#185a7d]"></i>
                  <span>Sin Spam</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
